import { useState } from 'react';

import { ScreenerResponseStatus } from '@/api/core-api/types.gen';

import type { TaskProgress } from '../../../-utils/task-progress';

import { CancelApplicationDialog } from './cancel-application-dialog';

/** Mirrors `cancellableStatuses` in core-api's `cancelApplication`. */
const CANCELLABLE_STATUSES: ReadonlySet<ScreenerResponseStatus | undefined> =
  new Set([
    ScreenerResponseStatus.PENDING,
    ScreenerResponseStatus.INVITED,
    ScreenerResponseStatus.SCHEDULED,
  ]);

interface CancelApplicationPromptProps {
  projectId: string;
  userId: string;
  projectName: string;
  screenerResponseStatus: ScreenerResponseStatus | undefined;
  kindOfResearch: number | undefined;
  taskProgress: TaskProgress | null;
  respondentRemuneration?: number;
}

/**
 * Confirmation prompt for `?cancel=true` deep-links — the "Cancel my spot"
 * links in invitation emails (and the legacy
 * `/respondents/v2/surveys?id=…&cancel=true` URLs in already-sent emails,
 * which the website redirects here). The legacy Vue portal auto-opened a
 * cancel modal for these links; without this, the parameter would be silently
 * ignored (ONCALL-2152).
 *
 * Only renders while the application is in a status core-api will actually
 * cancel, so a refresh after cancelling (or a stale link for a completed
 * project) asks nothing.
 */
export function CancelApplicationPrompt({
  projectId,
  userId,
  projectName,
  screenerResponseStatus,
  kindOfResearch,
  taskProgress,
  respondentRemuneration,
}: CancelApplicationPromptProps) {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed || !CANCELLABLE_STATUSES.has(screenerResponseStatus)) {
    return null;
  }

  return (
    <CancelApplicationDialog
      isOpen
      onClose={() => setDismissed(true)}
      projectId={projectId}
      userId={userId}
      projectName={projectName}
      status={screenerResponseStatus}
      kindOfResearch={kindOfResearch}
      taskProgress={taskProgress}
      respondentRemuneration={respondentRemuneration}
    />
  );
}
